import React, { useState, useEffect } from 'react'
import {
  Box,
  VStack,
  Text,
  Button,
  Flex,
  Spinner,
  Portal,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../ui/Icon'
import { Tooltip } from '../ui/Tooltip'
import { useNavigate, useLocation } from 'react-router-dom'
import { useProfiles } from '../../hooks/useProfiles'
import { useAuth } from '../../contexts/AuthContext'
import { useActiveProfile } from '../../contexts/ActiveProfileContext'
import { useNavigationState } from '../../hooks/useNavigationState'
import { getAdminStatusFromUser } from '../../utils/jwtDecoder'
import { useFeatureAccess } from '../../hooks/useFeatureAccess'
import { Logo } from '../Brand/Logo'
import { FiChevronRight, FiChevronDown } from 'react-icons/fi'
import { 
  getContextAwareProfilePath, 
  showProfileSwitchNotification,
  getCurrentPageType,
  shouldMaintainContext,
  hasUnsavedChanges
} from '../../utils/profileNavigation'
import { toaster } from '../ui/toaster-instance'
import { coachNavTarget } from '../../utils/coachNavigation'

interface MenuItem {
  name: string
  icon?: string
  href?: string
  children?: MenuItem[]
}

interface SidebarContentProps {
  onClose?: () => void
  isCollapsed?: boolean
  variant?: 'drawer' | 'sidebar'
}

const SidebarContent: React.FC<SidebarContentProps> = ({ onClose, isCollapsed, variant = 'sidebar' }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { recentProfiles, allProfiles, hasMoreThan5, isLoading, error } = useProfiles()
  const { currentUser } = useAuth()
  const { activeProfile, setActiveProfile } = useActiveProfile()
  const { expandedSections, toggleSection } = useNavigationState()
  const { plan, features } = useFeatureAccess()
  
  // Admin claim detection (mirrors UserDropdown behavior)
  const [isAdmin, setIsAdmin] = useState(false)
  
  // Check if user has Pro plan (check plan name or client_management feature)
  const hasProAccess = plan === 'pro' || features.includes('client_management')

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        setIsAdmin(false)
        return
      }
      // Use utility function to avoid network requests when checking admin status
      const adminStatus = await getAdminStatusFromUser(currentUser)
      setIsAdmin(adminStatus)
    }
    checkAdminStatus()
  }, [currentUser])

  // Helper function to close sidebar only on mobile (drawer mode)
  const closeOnMobile = () => {
    if (variant === 'drawer') {
      onClose?.()
    }
  }
  
  // Helper function to get the current active profile ID
  const getCurrentProfileId = () => {
    return activeProfile?.profileId || recentProfiles[0]?.profileId
  }

  // Collapsible sections with sub-menus
  const collapsibleSections: { id: string; label: string; icon: string; items?: MenuItem[]; custom?: boolean; isStandalone?: boolean; href?: string }[] = [
    ...(hasProAccess ? [{
      id: 'pro-dashboard',
      label: 'Pro Dashboard',
      icon: 'briefcase',
      isStandalone: true,
      custom: true, // Mark as custom to handle visibility
    }] : []),
    {
      id: 'admin',
      label: t('dashboard.sidebar.admin_dashboard'),
      icon: 'shield',
      isStandalone: true,
      custom: true, // Mark as custom to handle visibility
    },
    {
      id: 'genechat',
      label: 'Coach',
      icon: 'sparkling-fill',
      isStandalone: true,
      href: '/chat',
    },
    {
      // Discovery's in-app channel already outperforms email; give it a named
      // entry instead of relying on the unlabelled navbar compass.
      id: 'discoveries',
      label: 'Discoveries',
      icon: 'compass',
      isStandalone: true,
      href: '/discoveries',
    },
    {
      id: 'wellnessGoals',
      label: t('dashboard.sidebar.wellness_goals'),
      icon: 'target',
      isStandalone: true,
      href: '/wellness-goals',
    },
    {
      id: 'healthRecords',
      label: t('dashboard.sidebar.health_records'),
      icon: 'clipboard',
      isStandalone: true,
      href: '/health-records',
    },
    {
      id: 'genetics',
      label: t('dashboard.sidebar.genetics'),
      icon: 'dna',
      items: [
        { name: t('dashboard.sidebar.genotype_reports'), icon: 'clipboard', href: '/genetic-reports' },
        { name: t('dashboard.sidebar.comprehensive_report'), icon: 'file-text', href: '/genetic-reports/comprehensive' },
        { name: t('dashboard.sidebar.custom_reports'), icon: 'file-text', href: '/genetic-reports/custom-reports' },
        { name: t('dashboard.sidebar.community_templates'), icon: 'database', href: '/snp-library' },
      ]
    },
    // Messages (Standalone)
    {
      id: 'messages',
      label: t('dashboard.navbar.messages', 'Messages'),
      icon: 'mail',
      isStandalone: true,
      href: '/messages',
    },
    // Support (Standalone)
    {
      id: 'support',
      label: t('dashboard.navbar.support'),
      icon: 'help-circle',
      isStandalone: true,
      href: '/support',
    }
  ]

  // Top-level menu items (no sub-menus)
  const topLevelItems: MenuItem[] = [
    // No Documents here anymore
  ]

  const normalizePath = (path?: string) => (path || '').split('?')[0].split('#')[0]
  const getHash = (path?: string) => {
    const parts = (path || '').split('#')
    return parts.length > 1 ? `#${parts[1]}` : ''
  }
  const messagingPaths = ['/messages', '/pro/messages']

  const isActivePath = (path: string) => {
    const normalized = normalizePath(path)
    const targetHash = getHash(path)
    if (!normalized) return false
    if (messagingPaths.includes(normalized)) {
      return messagingPaths.some(
        (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
      )
    }
    if (targetHash) {
      return location.pathname === normalized && location.hash === targetHash
    }
    return location.pathname === normalized || location.pathname.startsWith(`${normalized}/`)
  }

  const isActiveItem = (item: MenuItem) => {
    // Admin Tools entry
    if (item.name === t('dashboard.sidebar.admin_dashboard')) {
      return location.pathname.startsWith('/admin')
    }

    // Special handling for Pro Dashboard
    if (item.name === 'Pro Dashboard') {
      return location.pathname.startsWith('/pro/')
    }

    // Special handling for Health Records page (includes documents tab and document detail)
    if (item.name === t('dashboard.sidebar.health_records')) {
      return location.pathname.includes('/health-records') || location.pathname.startsWith('/documents')
    }
    
    // Special handling for Wellness Goals page
    if (item.name === t('dashboard.sidebar.wellness_goals')) {
      return location.pathname.includes('/wellness-goals')
    }
    
    // Genetics sub-nav — each item maps to a distinct route
    if (item.name === t('dashboard.sidebar.genotype_reports')) {
      return location.pathname === '/genetic-reports'
    }
    if (item.name === t('dashboard.sidebar.comprehensive_report')) {
      return location.pathname.startsWith('/genetic-reports/comprehensive')
    }
    if (item.name === t('dashboard.sidebar.custom_reports')) {
      return location.pathname.startsWith('/genetic-reports/custom-reports')
        || location.pathname.startsWith('/genetic-reports/custom')
    }
    if (item.name === t('dashboard.sidebar.community_templates')
        || item.name === t('dashboard.sidebar.snp_library')) {
      return location.pathname === '/snp-library' || location.pathname.startsWith('/snp-library/')
    }
    
    if (item.href && messagingPaths.includes(normalizePath(item.href))) {
      return messagingPaths.some(
        (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
      )
    }
    
    // Hash-specific items (e.g., /account#info) should only match their exact hash
    if (item.href?.includes('#')) {
      const normalized = normalizePath(item.href)
      const hash = getHash(item.href)
      return location.pathname === normalized && location.hash === hash
    }
    
    // Regular path matching for items with href
    if (item.href && item.href !== '#') {
      return isActivePath(item.href)
    }
    
    return false
  }



  const handleItemClick = (item: MenuItem) => {
    // Admin Tools navigation
    if (item.name === t('dashboard.sidebar.admin_dashboard')) {
      navigate('/admin/users')
      closeOnMobile()
      return
    }

    // Handle Pro Dashboard navigation
    if (item.name === 'Pro Dashboard') {
      navigate('/pro/clients')
      closeOnMobile()
      return
    }

    if (item.name === 'Coach') {
      const currentProfileId = getCurrentProfileId()
      if (currentProfileId || recentProfiles.length > 0) {
        navigate(coachNavTarget(location.pathname))
        closeOnMobile()
      } else if (!isLoading) {
        navigate('/profiles')
        closeOnMobile()
      }
      return
    }

    // Handle Health Records navigation
    if (item.name === t('dashboard.sidebar.health_records')) {
      const currentProfileId = getCurrentProfileId()
      
      if (currentProfileId) {
        navigate('/health-records')
        closeOnMobile()
      } else if (recentProfiles.length > 0) {
        // If no current profile but profiles exist, use the first one
        navigate('/health-records')
        closeOnMobile()
      } else if (!isLoading) {
        // Only go to profiles page if there are genuinely no profiles and not loading
        navigate('/profiles')
        closeOnMobile()
      }
      return
    }

    // Handle Wellness Goals navigation
    if (item.name === t('dashboard.sidebar.wellness_goals')) {
      const currentProfileId = getCurrentProfileId()
      
      if (currentProfileId) {
        navigate('/wellness-goals')
        closeOnMobile()
      } else if (recentProfiles.length > 0) {
        // If no current profile but profiles exist, use the first one
        navigate('/wellness-goals')
        closeOnMobile()
      } else if (!isLoading) {
        // Only go to profiles page if there are genuinely no profiles and not loading
        navigate('/profiles')
        closeOnMobile()
      }
      return
    }
    
    // Handle regular navigation for items with valid hrefs
    if (item.href && item.href !== '#') {
      navigate(item.href)
      closeOnMobile()
    }
  }

  const hasActiveChild = (section: { items?: MenuItem[] }) =>
    section.items?.some((item) => isActiveItem(item)) ?? false

  // If collapsed, show only icons (no flyouts) with Pro-style active indicator
  if (isCollapsed) {
    return (
      <Box h="full" overflow="visible" position="relative">
        <VStack align="center" gap={2} mt={8} overflow="visible">
          {collapsibleSections.map((section) => {
            if (
              (section.id === 'admin' && !isAdmin) ||
              (section.id === 'pro-dashboard' && !hasProAccess)
            ) {
              return null
            }

            const standaloneItem = { name: section.label, icon: section.icon as any, href: section.href }
            const active = section.isStandalone ? isActiveItem(standaloneItem) : hasActiveChild(section)

            // Determine default navigation for non-standalone sections
            const getDefaultNavigation = () => {
              if (section.id === 'genetics') return '/genetic-reports'
              return null
            }

            return (
              <Tooltip
                key={section.id}
                label={section.label}
                placement="right"
                hasArrow
              >
                <Box position="relative">
                  <Button
                    variant="ghost"
                    size="lg"
                    p={0}
                    h="48px"
                    w="48px"
                    justifyContent="center"
                    alignItems="center"
                    position="relative"
                    bg={active ? 'brand.50' : undefined}
                    color={active ? 'brand.600' : 'gray.600'}
                    _before={
                      active
                        ? {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            bg: 'brand.600',
                            borderRightRadius: '4px',
                          }
                        : undefined
                    }
                    _hover={{
                      bg: 'gray.100',
                      color: active ? 'brand.600' : 'gray.900',
                    }}
                    _focusVisible={{
                      outline: '2px solid',
                      outlineColor: 'brand.500',
                      outlineOffset: '2px',
                    }}
                    borderRadius={0}
                    onClick={() => {
                      if (section.isStandalone) {
                        handleItemClick(standaloneItem)
                      } else {
                        // Navigate to default page for non-standalone sections
                        const defaultHref = getDefaultNavigation()
                        if (defaultHref) {
                          navigate(defaultHref)
                          closeOnMobile()
                        }
                      }
                    }}
                    aria-label={section.label}
                  >
                    <Icon name={section.icon} size="sm" />
                  </Button>
                </Box>
              </Tooltip>
            )
          })}
        </VStack>
      </Box>
    )
  }

  // Expanded state - show full sidebar content with hover-based submenus
  return (
    <Box h="full">
      <VStack align="stretch" gap={2} mt={8}>
        {/* Collapsible Sections */}
        {collapsibleSections.map((section) => {
          // Skip admin section if user is not admin
          if (section.id === 'admin' && !isAdmin) {
            return null
          }
          
          // Skip Pro Dashboard section if user doesn't have Pro access
          if (section.id === 'pro-dashboard' && !hasProAccess) {
            return null
          }

          // Standalone section (e.g., Documents, GeneChat) is not collapsible
          if (section.isStandalone) {
            const standaloneItem = { name: section.label, icon: section.icon as any, href: section.href }
          const active = isActiveItem(standaloneItem)
            return (
              <Box key={section.id}>
                <Button
                  id={`header-${section.id}`}
                  variant="ghost"
                  justifyContent="space-between"
                  px={4}
                  py={3}
                  h="auto"
                  w="full"
                  fontWeight="medium"
                  fontSize="sm"
                color={active ? 'brand.600' : 'gray.600'}
                bg={active ? 'brand.50' : undefined}
                position="relative"
                _before={
                  active
                    ? {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        bg: 'brand.600',
                        borderRightRadius: '4px',
                      }
                    : undefined
                }
                _hover={{
                  bg: 'gray.100',
                  color: active ? 'brand.600' : 'gray.900',
                }}
                borderRadius={0}
                  onClick={() => handleItemClick(standaloneItem)}
                  role="menuitem"
                aria-current={active ? 'page' : undefined}
                >
                  <Flex alignItems="center" gap={3}>
                    <Icon name={section.icon} size="xs" />
                    <Text>{section.label}</Text>
                  </Flex>
                </Button>
              </Box>
            )
          }
          
          const hasActive = hasActiveChild(section)
          // Genetics defaults open for a profile with no DNA yet, so upload
          // is visible without an extra click — collapsed is the right
          // default once there's a report underneath it to hide.
          const defaultExpanded = section.id === 'genetics' && activeProfile?.hasRawdata === false
          const isExpanded = expandedSections[section.id] ?? defaultExpanded
          const ChevronIcon = isExpanded ? FiChevronDown : FiChevronRight

          return (
            <Box key={section.id}>
              {/* Section Header - click to expand/collapse */}
              <Button
                id={`header-${section.id}`}
                variant="ghost"
                justifyContent="space-between"
                px={4}
                py={3}
                h="auto"
                w="full"
                fontWeight="medium"
                fontSize="sm"
                color={hasActive ? 'brand.600' : 'gray.600'}
                bg={hasActive ? 'brand.50' : undefined}
                position="relative"
                _before={
                  hasActive
                    ? {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        bg: 'brand.600',
                        borderRightRadius: '4px',
                      }
                    : undefined
                }
                _hover={{
                  bg: 'gray.100',
                  color: hasActive ? 'brand.600' : 'gray.900',
                }}
                borderRadius={0}
                onClick={() => toggleSection(section.id)}
                aria-expanded={isExpanded}
                aria-controls={`section-${section.id}`}
                role="button"
                aria-label={t(`dashboard.sidebar.${isExpanded ? 'collapse' : 'expand'}_section`, { section: section.label })}
              >
                <Flex alignItems="center" gap={3}>
                  <Icon name={section.icon} size="xs" />
                  <Text>{section.label}</Text>
                </Flex>
                <ChevronIcon size="12px" color="currentColor" />
              </Button>

              {/* Collapsible Section Content */}
              {isExpanded && (
                <Box
                  id={`section-${section.id}`}
                  role="region"
                  aria-labelledby={`header-${section.id}`}
                  mt={1}
                >
                  <VStack align="stretch" gap={1}>
                    {section.items?.map((item) => {
                      const active = isActiveItem(item)
                      return (
                        <Button
                          key={item.name}
                          variant="ghost"
                          justifyContent="flex-start"
                          px={6}
                          py={2}
                          h="auto"
                          fontWeight="medium"
                          fontSize="sm"
                          color={active ? 'brand.600' : 'gray.600'}
                          bg={active ? 'brand.50' : undefined}
                          position="relative"
                          _before={
                            active
                              ? {
                                  content: '""',
                                  position: 'absolute',
                                  left: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: '4px',
                                  bg: 'brand.600',
                                  borderRightRadius: '4px',
                                }
                              : undefined
                          }
                          _hover={{
                            bg: 'gray.100',
                            color: active ? 'brand.600' : 'gray.900',
                          }}
                          borderRadius={0}
                          onClick={() => handleItemClick(item)}
                          role="menuitem"
                          aria-current={active ? 'page' : undefined}
                          disabled={item.href === '#' && item.name !== t('dashboard.sidebar.documents') && item.name !== t('dashboard.sidebar.genotype_reports') && item.name !== t('dashboard.sidebar.snp_library')} // Don't disable Documents, Genotype Reports, and SNP Library
                          cursor={item.href === '#' && item.name !== t('dashboard.sidebar.documents') && item.name !== t('dashboard.sidebar.genotype_reports') && item.name !== t('dashboard.sidebar.snp_library') ? 'not-allowed' : 'pointer'}
                          opacity={item.href === '#' && item.name !== t('dashboard.sidebar.documents') && item.name !== t('dashboard.sidebar.genotype_reports') && item.name !== t('dashboard.sidebar.snp_library') ? 0.6 : 1}
                        >
                          <Flex alignItems="center" gap={3}>
                            {item.icon && <Icon name={item.icon} size="xs" />}
                            {item.name}
                          </Flex>
                        </Button>
                      )
                    })}
                  </VStack>
                </Box>
              )}
            </Box>
          )
        })}

        {/* Top-level Menu Items */}
        <Box mt={4}>
          <VStack align="stretch" gap={1}>
            {topLevelItems.map((item) => (
              <Button
                key={item.name}
                variant="ghost"
                justifyContent="flex-start"
                px={4}
                py={3}
                h="auto"
                fontWeight="medium"
                color={
                  isActiveItem(item)
                    ? 'brand.700' 
                    : 'fg.muted'
                }
                bg={
                  isActiveItem(item)
                    ? 'brand.50' 
                    : 'transparent'
                }
                _hover={{
                  bg: 'wellness.mint',
                  color: 'brand.700',
                }}
                _focus={{
                  bg: 'brand.50',
                  color: 'brand.700',
                }}
                onClick={() => handleItemClick(item)}
                role="menuitem"
                aria-current={
                  isActiveItem(item) ? 'page' : undefined
                }
                disabled={item.href === '#' && item.name !== 'Documents' && item.name !== 'Genotype Reports' && item.name !== 'SNP Library'} // Don't disable Documents, Genotype Reports, and SNP Library
                cursor={item.href === '#' && item.name !== 'Documents' && item.name !== 'Genotype Reports' && item.name !== 'SNP Library' ? 'not-allowed' : 'pointer'}
                opacity={item.href === '#' && item.name !== 'Documents' && item.name !== 'Genotype Reports' && item.name !== 'SNP Library' ? 0.6 : 1}
              >
                <Flex alignItems="center" gap={3}>
                  {item.icon && <Icon name={item.icon} size="xs" />}
                  {item.name}
                </Flex>
              </Button>
            ))}
          </VStack>
        </Box>

      </VStack>

    </Box>
  )
}

interface DashboardSidebarProps {
  isOpen: boolean
  onClose: () => void
  variant?: 'drawer' | 'sidebar'
  isCollapsed?: boolean
}

export const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  isOpen,
  onClose,
  variant = 'sidebar',
  isCollapsed = false,
}) => {
  const isDrawer = variant === 'drawer'

  // Lock body scroll when drawer is open on mobile
  useEffect(() => {
    if (isDrawer && isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isDrawer, isOpen])

  if (isDrawer) {
    return (
      <>
        {/* Backdrop overlay */}
        {isOpen && (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.600"
            zIndex={1400}
            onClick={onClose}
            aria-hidden="true"
            transition="opacity 0.3s"
          />
        )}

        {/* Drawer */}
        <Box
          position="fixed"
          top={0}
          left={isOpen ? 0 : '-100%'}
          w="240px"
          h="100vh"
          bg="bg"
          borderRight="1px"
          borderColor="border.muted"
          shadow="xl"
          transition="left 0.3s ease-in-out"
          zIndex={1500}
          role="navigation"
          aria-label="Mobile sidebar navigation"
          overflowY="auto"
        >
          <Box p={4} pt="68px">
            <Flex justifyContent="space-between" alignItems="center" mb={4}>
              <Logo size="md" />
              <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close sidebar">
                ✕
              </Button>
            </Flex>
            <SidebarContent onClose={onClose} isCollapsed={false} variant="drawer" />
          </Box>
        </Box>
      </>
    )
  }

  return (
    <Box
      as="aside"
      position="fixed"
      left={0}
      w={isCollapsed ? '64px' : '240px'}
      h="100vh" // Full viewport height
      top={0} // Start from the very top
      bg="bg"
      borderRight="1px"
      borderColor="border.muted"
      shadow="sm"
      overflow="visible" // Allow flyout menus to show outside
      role="navigation"
      aria-label="Sidebar navigation"
      data-nav="sidebar"
      zIndex="docked" // Ensure sidebar is above content
    >
      <Box p={isCollapsed ? 2 : 4} pt="68px" overflow="visible"> {/* Add top padding to account for navbar */}
        <SidebarContent onClose={onClose} isCollapsed={isCollapsed} variant="sidebar" />
      </Box>
    </Box>
  )
} 
